import { Box, Button, CardMedia, CircularProgress, Divider, IconButton, TextField, Typography, useMediaQuery, useTheme, Dialog } from "@mui/material";
import CloseIcon from "../../svg/CloseIcon";
import { useAuth } from "../../hooks/useAuth";
import CustomAvatar from "../../component/CustomAvatar";
import { useEffect, useState } from "react";
import { createPost, updatePost } from "../../utils/apis/memeverse";
import AddImageIcon from "../../svg/MemeverseIcons/AddImageIcon";
import ImageItem from "./imageItem";
import { MODERATION_ERROR, PostType } from "./memeverse_types";
import GenerateIcon2 from "../../svg/MemeverseIcons/GenerateIcon2";
import GenerateMemes from "./GenerateMemes";
import ModeratedContent from "./ModeratedContent";
import { logging } from "../../utils/logger";

interface CreateUpdatePostProps {
  createUpdatePostVisible: boolean;
  setCreateUpdatePostVisible: (val: boolean) => void;
  existingPost?: PostType;
}

const CreateUpdatePost: React.FC<CreateUpdatePostProps> = (props) => {
  const {
    createUpdatePostVisible,
    setCreateUpdatePostVisible,
    existingPost
  } = props;
  const { authUser } = useAuth();
  let oldData = existingPost?.content[existingPost?.content.length-1].content_body || '';
  let existingImages = existingPost?.images;
  const [ postContent, setPostContent ] = useState<string>(oldData);
  const [ isSaving, setIsSaving ] = useState<boolean>(false);
  const [ images, setImages ] = useState<(File | string)[]>(existingImages || []);
  const [ showGenerateMemes, setShowGenerateMemes ] = useState<boolean>(false);
  const [ failedModeration, setFailedModeration ] = useState<boolean>(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const handleMemeGenerated = (memeUrl: string) => {
    setImages([memeUrl]);
  };

  const handleClose = (newData?: {updatedContent?: string, updatedImages?: (File | string)[]}) => {
    setPostContent(newData?.updatedContent || oldData);
    setImages(newData?.updatedImages || existingImages || []);
    setShowGenerateMemes(false);
    setFailedModeration(false);
    setCreateUpdatePostVisible(false);
  }

  const handleSavePost = async () => {
    if (postContent.length <= 300) {
      if (!existingPost && postContent) {
        setIsSaving(true)
        createPost(authUser.uid, postContent, images as File[])
          .then(async (resp) => {
            if (resp.ok) {
              handleClose({
                updatedContent: postContent,
                updatedImages: images
              });
            } else {
              let data = await resp.json();
              if (data.error === MODERATION_ERROR) {
                logging.info('Post failed moderation', {
                  context: {
                    postContent,
                    images
                  },
                  user: {
                    id: authUser?.uid,
                    email: authUser?.email,
                    username: authUser?.userName
                  }
                })
                setFailedModeration(true)
              }
            }
          }, () => {})
          .catch((err) => {
            logging.error('Error creating post', {
              context: {
                error: err,
                postContent,
                images
              }
            })
          })
          .finally(() => {
            setIsSaving(false);
          })
      } else if (existingPost) {
        if (oldData !== postContent) {
          // start editing
          setIsSaving(true)
          updatePost(authUser.uid, existingPost.docId, postContent)
            .then(async (resp) => {
              if (resp.ok) {
                handleClose({
                  updatedContent: postContent,
                  updatedImages: images
                });
              } else {
                let data = await resp.json();
                if (data.error === MODERATION_ERROR) {
                  logging.info('Post failed moderation', {
                    context: {
                      postContent,
                      images
                    },
                    user: {
                      id: authUser?.uid,
                      email: authUser?.email,
                      username: authUser?.userName
                    }
                  })
                  setFailedModeration(true)
                }
              }
            }, () => {})
            .catch((err) => {
              logging.error('Error creating post', {
                context: {
                  error: err,
                  postContent,
                  images
                }
              })
            })
            .finally(() => {
              setIsSaving(false);
            })
        }
      }
    }
  };

  const removeImage = (index: number) => {
    let newImages = images.filter((_ , i) => i !== index);
    setImages(newImages);
  }

  const MobileHeader = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          mt: -1,
          px: 1.5,
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 'solid 1px',
          borderColor: theme.palette.divider,
          py: 1
        }}
      >
        <Button
          size='medium'
          onClick={() => handleClose()}
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          Cancel
        </Button>
        <Box
          gap={2}
          display='flex'
          alignItems='center'
        >
          <Typography
            variant='body2'
            color={postContent.length <= 300 ? 'text.secondary' : 'error.main'}
          >
            {`${postContent.length}/300`}
          </Typography>
          <Button
            variant="contained"
            disabled={postContent === oldData || isSaving || postContent.length > 300}
            onClick={() => handleSavePost()}
            startIcon={isSaving ?
              <CircularProgress size={20} color='inherit'/> :
              <></>
            }
          >
            Post
          </Button>
        </Box>
      </Box>
    )
  }

  const RegularHeader = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h7" color='text.primary'>
          {existingPost ? 'Edit Post' : 'New Post'}
        </Typography>
        <IconButton
          onClick={() => handleClose()}
          sx={{
            stroke: (theme) => theme.palette.action.active,
          }}
        >
          <CloseIcon strokeWidth={2} stroke={'inherit'}/>
        </IconButton>
      </Box>
    )
  }

  return (
    failedModeration ?
    <Dialog
      open={createUpdatePostVisible}
      onClose={() => handleClose()}
      fullWidth
      maxWidth='sm'
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <ModeratedContent
        postContent={postContent}
        images={images}
        handleDelete={() => {
          handleClose();
        }}
        handleEdit={() => {
          setFailedModeration(false);
        }}
      />
    </Dialog>:
    <Dialog
      open={createUpdatePostVisible}
      onClose={() => handleClose()}
      fullWidth
      maxWidth='md'
      slotProps={{
        backdrop: {
          sx: {
            display: { xs: 'none', md: 'block' },
          },
        },
      }}
      PaperProps={{
        sx: {
          m: 0,
          [theme.breakpoints.down('md')]: {
            width: '100%',
            maxWidth: '100%',
            height: 'calc(100% - 107px)',
            maxHeight: 'none',
            boxShadow: 'none',
            mt: '50px',
          },
        },
      }}
      sx={{
        [theme.breakpoints.down('md')]: {
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
          },
        },
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <Box
        className="new-post"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          px: {xs: 0, md: 2.5},
          py: {xs: 1, md: 2},
          [theme.breakpoints.down('md')]: {
            height: '100%',
          },
        }}
      >
        { isSmallScreen ? <MobileHeader /> : <RegularHeader /> }

        <Box
          display='flex'
          width='100%'
          gap={2}
          px={2.5}
          flexGrow={1}
        >
          <CustomAvatar src={authUser?.profilePicUrl} sx={{ width: 48, height: 48 }} />
          <Box
            display='flex'
            flexDirection='column'
            width='100%'
          >
            <TextField
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              placeholder="What is your question?"
              fullWidth
              multiline
              minRows={images.length ? 1 : 4}
              variant="standard"
              slotProps={{
                input: {
                  disableUnderline: true,
                  sx: {
                    [theme.breakpoints.down('md')]: {
                      height: images.length ? 'auto' : '100%',
                      '& textarea': {
                        height: images.length ? 'auto' : '100% !important',
                      }
                    }
                  }
                }
              }}
              sx={{
                [theme.breakpoints.down('md')]: {
                  height: images.length ? 'auto' : '100%',
                }
              }}
            />
            {
              images.map((image, index) => {
                return (
                  <Box
                    key={`new-post-image-${index}`}
                    maxWidth={'100%'}
                    width={'100%'}
                    position='relative'
                  >
                    {!existingPost &&
                      <IconButton
                        onClick={() => removeImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 3,
                          right: 3,
                          background: (theme) => theme.palette.primary.main,
                          stroke: (theme) => theme.palette.primary.contrastText,
                          zIndex: 3,
                        }}
                      >
                        <CloseIcon strokeWidth={2} stroke={'inherit'}/>
                      </IconButton>
                    }
                    {
                      (typeof image === 'string') ?
                      <ImageItem
                        image={image}
                        alt={`image-${index}`}
                      /> :
                      <ImageItem
                        image={URL.createObjectURL(image)}
                        alt={`image-${index}`}
                      />}
                  </Box>
                )
              })
            }
          </Box>
        </Box>

        <Divider />

        <Box
          display='flex'
          width='100%'
          justifyContent={'space-between'}
          alignItems={'center'}
          sx={{
            px: {xs: 2, md: 0}
          }}
        >
          <Box
            display='flex'
            alignItems={'center'}
          >
            <Button
              size='medium'
              component='label'
              startIcon={<AddImageIcon />}
              disabled={!!existingPost || images.length >= 1}
            >
              Add Photo
              <input
                type='file'
                accept='image/*'
                hidden
                multiple={false}
                id='file-input-images'
                onChange={(event) => {
                  const files = event?.target?.files;
                  if (!files || files.length === 0) return;
                  const file = files[0];
                  setImages([file]);
                }}
              />
            </Button>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ ml: 0.5 }}
          >
            (Limit 1 photo)
          </Typography>
            <Button
              size='medium'
              component='label'
              onClick={() => setShowGenerateMemes(true)}
              startIcon={<GenerateIcon2 />}
              disabled={!!existingPost || images.length >= 1}
            >
              Generate Meme
            </Button>
          </Box>
          <Box
            gap={2}
            alignItems='center'
            sx={{
              display: {xs: 'none', md: 'flex'},
            }}
          >
            <Typography
              variant='body2'
              color={postContent.length <= 300 ? 'text.secondary' : 'error.main'}
            >
              {`${postContent.length}/300`}
            </Typography>
            <Button
              variant="contained"
              disabled={postContent === oldData || isSaving || postContent.length > 300}
              onClick={() => handleSavePost()}
              startIcon={isSaving ?
                <CircularProgress size={20} color='inherit'/> :
                <></>
              }
            >
              Post
            </Button>
          </Box>
        </Box>

        <Dialog
          open={showGenerateMemes}
          onClose={() => setShowGenerateMemes(false)}
          fullWidth
          maxWidth='md'
          slotProps={{
            backdrop: {
              sx: {
                display: {xs: 'none', md: 'block'},
              },
            },
          }}
          PaperProps={{
            sx: {
              m: 0,
              [theme.breakpoints.down('md')]: {
                width: '100%',
                maxWidth: '100%',
                height: 'calc(100% - 107px)',
                maxHeight: 'none',
                boxShadow: 'none',
                mt: '50px',
              }
            }
          }}
          sx={{
            [theme.breakpoints.down('md')]: {
              '& .MuiDialog-container': {
                alignItems: 'flex-start'
              }
            }
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <GenerateMemes
            setGenerateMemesVisible={setShowGenerateMemes}
            onMemeGenerated={handleMemeGenerated}
            userId={authUser.uid}
            content={postContent}
          />
        </Dialog>
      </Box>
    </Dialog>
  )
}

export default CreateUpdatePost;
